import type {LabelSuggestion} from '@github-ui/item-picker-label'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {apiGetSearchLabels, transformSearchLabelResponseToQueryData} from './api-get-search-labels'
import type {Label} from './label'

const suggestionsUrlBase = '/_ghui/item-pickers/labels/suggestions'

export type GetLabelSuggestionsRequest = {
  owner: string
  repo: string
  includeArchived?: boolean
}

/**
 * Raw payload of the dedicated suggestions endpoint. The server owns ordering,
 * cross-source precedence and the cap, so entries are rendered in the order they
 * arrive.
 *
 * An unreadable or missing repository responds with `errors` instead.
 */
type GetLabelSuggestionsResponse = {
  suggestions?: Array<{label: Label; reason: string; rank: number} | null> | null
  errors?: Array<{message: string}>
}

export type LabelSuggestionsQueryData = {
  suggestions: LabelSuggestion[]
}

const itemPickerQueryKeyBase = 'item_picker'

/**
 * Keyed on the repository — deliberately *not* on the current selection or on
 * `labelNames`. Suggestions are a property of the viewer and the repo, so keying
 * them on selection would spawn a fresh cache entry (and a fresh request) on
 * every toggle. Which suggestions are hidden because they're already selected is
 * a pure render concern.
 *
 * ROLLOUT-CLEANUP(label_suggestions_endpoint): `includeArchived` is only in the
 * key because the legacy fallback below forwards it to `labels#index`, whose
 * response varies by it, so two surfaces that disagree must not share an entry.
 * The dedicated endpoint does not take it — the server is trusted to keep
 * archived labels out of suggestions. Drop it from the key with the fallback.
 */
export function buildItemPickerLabelSuggestionsQueryKey({owner, repo, includeArchived}: GetLabelSuggestionsRequest) {
  return [itemPickerQueryKeyBase, 'labels', 'suggestions', {owner, repo, includeArchived}] as const
}

function toSuggestions(entries: GetLabelSuggestionsResponse['suggestions']): LabelSuggestion[] {
  return (entries ?? [])
    .filter((entry): entry is {label: Label; reason: string; rank: number} => entry?.label != null)
    .map(entry => ({label: entry.label, reason: entry.reason, rank: entry.rank}))
}

async function apiGetLabelSuggestions({owner, repo}: GetLabelSuggestionsRequest): Promise<LabelSuggestion[]> {
  const requestUrl = `${suggestionsUrlBase}?${new URLSearchParams({owner, repo}).toString()}`
  const request = await reactFetchJSON(requestUrl)
  const response = (await request.json()) as GetLabelSuggestionsResponse

  if (response.errors?.length) {
    throw new Error(response.errors[0]?.message ?? 'Error in request')
  }

  return toSuggestions(response.suggestions)
}

/**
 * ROLLOUT-CLEANUP(label_suggestions_endpoint): the dedicated endpoint is behind
 * its own server flag, ANDed with the flag gating the suggestions experience
 * itself. Neither is readable from the client, so an empty response is
 * ambiguous: the endpoint may be dark for this viewer while `labels#index` is
 * still serving suggestions through the legacy `suggested=true` embed.
 *
 * Falling back on an empty response keeps suggestions working for viewers who
 * are in the experience but not yet in the endpoint rollout. Delete this
 * fallback — and the `suggested` parameter it relies on — once the endpoint is
 * fully ramped.
 */
async function fetchLegacyEmbeddedSuggestions({
  owner,
  repo,
  includeArchived,
}: GetLabelSuggestionsRequest): Promise<LabelSuggestion[]> {
  const responseJson = await apiGetSearchLabels({owner, repo, query: '', suggested: true, includeArchived})
  if ('errors' in responseJson) return []

  return transformSearchLabelResponseToQueryData(responseJson.data).suggestedLabels ?? []
}

export async function fetchLabelSuggestions({
  owner,
  repo,
  includeArchived,
}: GetLabelSuggestionsRequest): Promise<LabelSuggestionsQueryData> {
  try {
    const suggestions = await apiGetLabelSuggestions({owner, repo})
    if (suggestions.length > 0) {
      return {suggestions}
    }
  } catch {
    // A backend without the endpoint deployed answers with a failure rather than
    // an empty list, which is the same rollout gap the fallback below exists to
    // cover, so a failed primary request has to fall through rather than abort.
  }

  try {
    return {suggestions: await fetchLegacyEmbeddedSuggestions({owner, repo, includeArchived})}
  } catch {
    // The picker renders fine without suggestions, so exhausting both sources
    // must not surface as an error state.
    return {suggestions: []}
  }
}
